'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { State } from 'country-state-city';
import { ChevronDown } from 'lucide-react';

export interface StateData {
  state_name: string;
  state_iso_code: string;
}

interface StateSelectProps {
  countryCode: string;
  value?: string; // state ISO code
  onChange?: (data: StateData) => void;
  label?: string;
  required?: boolean;
  error?: string;
  className?: string;
}

export default function StateSelect({
  countryCode,
  value,
  onChange,
  label = 'State / Province',
  required = false,
  error,
  className = '',
}: StateSelectProps) {
  const states = useMemo(
    () => (countryCode ? State.getStatesOfCountry(countryCode) : []),
    [countryCode]
  );
  const [selected, setSelected] = useState<string>(value || '');

  useEffect(() => {
    setSelected('');
    onChange?.({ state_name: '', state_iso_code: '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  useEffect(() => {
    if (value !== undefined) setSelected(value);
  }, [value]);

  if (!countryCode || states.length === 0) return null;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const isoCode = e.target.value;
    setSelected(isoCode);
    const state = states.find((s) => s.isoCode === isoCode);
    onChange?.({
      state_name: state?.name || '',
      state_iso_code: isoCode,
    });
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          value={selected}
          onChange={handleChange}
          className={`w-full px-4 py-2.5 border rounded-xl text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-all appearance-none pr-10 ${
            error ? 'border-red-400' : 'border-input'
          } ${!selected ? 'text-muted-foreground' : 'text-foreground'}`}
        >
          <option value="">Select state / province...</option>
          {states.map((state) => (
            <option key={state.isoCode} value={state.isoCode}>
              {state.name}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
